import { HOSPITALS_TABLE } from './../constants/dynamoDBTableName';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { client, dbClient, s3Client } from './awsClients';
import { AdminDeleteUserCommand } from "@aws-sdk/client-cognito-identity-provider";
import { BatchWriteItemCommand, BatchWriteItemCommandInput, BatchWriteItemCommandOutput, AttributeValue, UpdateItemCommand, UpdateItemInput, ScanCommand, PutItemCommand, ScanCommandInput, ScanCommandOutput, QueryCommandInput, QueryCommand, QueryCommandOutput } from '@aws-sdk/client-dynamodb';
import { PutObjectCommand, ObjectCannedACL, DeleteObjectCommand } from '@aws-sdk/client-s3';
import { v4 as uuidv4 } from 'uuid';
import { APPROVED, HOSPITAL, HOSPITAL_ID_CREATED_AT_INDEX, INDIVIDUAL, NURSE, STATUS_INDEX, TENANT_ID_CREATED_AT_INDEX, USER_ID_CREATED_AT_INDEX, VERIFIED } from '@constants/constants';
import { ADDRESS_1, ADDRESS_2, APPROVED_BY, APPROVED_ON, CITY, COUNTRY, CREATED_AT, CREATED_BY, DB_DEVICE_ID, DB_DEVICE_NAME, DB_DEVICE_STATUS, DB_DEVICE_TYPE, DB_EMAIL, DB_FIRST_LOGIN, DB_MOBILE, DB_STATUS, DB_USER_DEVICE_ID, DELETED_AT, DELETED_BY, FCM_TOKEN, HOSPITAL_BRANCH, HOSPITAL_CONTACT, HOSPITAL_EMAIL, HOSPITAL_GST, HOSPITAL_ID, HOSPITAL_LOGO, HOSPITAL_NAME, HOSPITAL_TELEPHONE, HOSPITAL_WEBSITE, MODIFIED_AT, MODIFIED_BY, NAME, NOTES, NURSE_ID, PINCODE, REASONS, ROLE, ROLE_ID, SLUG, STATE, STATUS, TENANT_ID, TENANT_NAME, TERMS_ACCEPTED, USER_ID, UUID } from '@constants/dynamoDBAttributes';
import awsConfig from '../amplifyconfiguration.json'
import { generateUniqueTimestamp } from './sharedUtils';

interface RequestItems {
  [tableName: string]: {
    PutRequest?: {
      Item: { [key: string]: AttributeValue }
    }
  }[]
}

export async function getUserAttributes() {
  try {
    const attributes = await fetchUserAttributes();
    // console.log('User attributes:', attributes.sub);
    return attributes;
  } catch (error: any) {
    // console.error('Error fetching user attributes:', error);
    let errorMessage = 'An unexpected error occurred while fetching user attributes';

    switch (error.name) {
      case 'ProvisionedThroughputExceededException':
        errorMessage = 'Provisioned throughput exceeded';
        break;
      case 'ResourceNotFoundException':
        errorMessage = 'Resource not found';
        break;
      default:
        errorMessage = error.message || 'An unknown error occurred';
    }

    // throw new Error(errorMessage);
    return errorMessage;
  }
}


async function batchWriteCommand(requestItems: any, data: any) {
  try {
    const params: BatchWriteItemCommandInput = {
      RequestItems: requestItems,
    };
    const command = new BatchWriteItemCommand(params);
    const response = await dbClient.send(command);
    // console.log('Batch write successful', response);
    return response;
  } catch (error: any) {
    throw error;
  }
}

function generateNurseId(name: string) {
  const firstTwoLetters = name.slice(0, 2); // Take the first two letters
  const baseSlug = firstTwoLetters
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric characters with hyphens
    .replace(/(^-|-$)+/g, ''); // Remove leading and trailing hyphens
  const uniqueId = uuidv4().split('-')[0]; // Take a part of the UUID to ensure uniqueness
  return `${baseSlug}-${uniqueId}`;
}

function generateHospitalId(name: string) {
  const firstTwoLetters = name.slice(0, 3);
  const baseSlug = firstTwoLetters
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');
  const uniqueId = uuidv4().split('-')[0];
  return `${baseSlug}-${uniqueId}`;
}

const getRolesForUser = async (roleName: string) => {
  const params = {
    TableName: 'roles',
    FilterExpression: '#role_name = :role_name',
    ExpressionAttributeNames: {
      "#role_name": "role_name"
    },
    ExpressionAttributeValues: {
      ':role_name': { S: roleName },
    }
  };
  const data = await dbClient.send(new ScanCommand(params));
  return data && data.Items ? data.Items[0].role_id?.S : '';
};

export function generateUUIDSegment(length: any) {
  return uuidv4().replace(/-/g, '').substring(0, length);
}

export function addUUIDSegmentToFilename(filename: string) {
  const uuidSegment = generateUUIDSegment(6);  // generates a 6-character UUID segment
  const dotIndex = filename.lastIndexOf('.');

  if (dotIndex === -1) {
    // No extension found
    return `${filename}_${uuidSegment}`;
  } else {
    const name = filename.substring(0, dotIndex);
    const extension = filename.substring(dotIndex);
    return `${name}_${uuidSegment}${extension}`;
  }
}


export async function userInfo(data: any) {
  const formattedDateTime = generateUniqueTimestamp();
  const userInfoUuid = data.userInfo_uuid ? data.userInfo_uuid : uuidv4();
  const hospitalUuid = data.hospital_uuid ? data.hospital_uuid : uuidv4();
  const hospitalTenantUuid = data.hospitalTenant_uuid ? data.hospitalTenant_uuid : uuidv4();
  const hospitalInfoUuid = data.hospitalInfo_uuid ? data.hospitalInfo_uuid : uuidv4();
  const roleUserUuid = uuidv4();

  const userRole = data.role;
  const userRoleID = await getRolesForUser(userRole.toLowerCase());
  const userName = data.name;
  const userAddressOne = data.addressOne;
  const userAddressTwo = data.addressTwo;
  const userCity = data.city;
  const userState = data.state;
  const userCountry = data.country;
  const userPincode = data.pincode;
  const updatedBy = data.updatedBy;
  // const hospitalID = hospital_uuid;
  const hospitalID = generateHospitalId(data.hospitalName.toLowerCase());
  const hospitalName = data.hospitalName;
  // const userSlug = generateSlug(userName);
  const userSlug = data.slug;
  const tenantUuid = data.tenant_id;
  const nurseId = generateNurseId(userName.toLowerCase());
  const logoName = addUUIDSegmentToFilename(data.logoName);

  let requestItems: RequestItems = {
    user_info: [],
    tenants: [],
    hospital_tenant: [],
    hospitals: [],
    hospital_info: [],
    role_user: [],
  };

  if (userRole.toLowerCase() === INDIVIDUAL.toLowerCase()) {
    requestItems.user_info.push({
      PutRequest: {
        Item: {
          [UUID]: { S: data.userInfo_uuid ? data.userInfo_uuid : userInfoUuid },
          // [UUID]: { S: userInfoUuid },
          [NAME]: { S: userName },
          [ADDRESS_1]: { S: userAddressOne },
          [ADDRESS_2]: { S: userAddressTwo },
          [CITY]: { S: userCity },
          [STATE]: { S: userState },
          [STATUS]: { S: VERIFIED },
          [COUNTRY]: { S: userCountry },
          [PINCODE]: { S: userPincode },
          [SLUG]: { S: userSlug },
          [DB_EMAIL]: { S: data.userEmail },
          [DB_MOBILE]: { S: data.userMobile },
          [DB_USER_DEVICE_ID]: { S: data.userDeviceId },
          [ROLE]: { S: userRole },
          [DB_FIRST_LOGIN]: { S: 'false' },
          [USER_ID]: { S: data.userId },
          [TENANT_ID]: { S: tenantUuid },
          [CREATED_BY]: { S: updatedBy },
          [CREATED_AT]: { S: formattedDateTime },
          [MODIFIED_BY]: { S: updatedBy },
          [MODIFIED_AT]: { S: formattedDateTime },
          [DELETED_BY]: { S: '' },
          [DELETED_AT]: { S: '' },
          [TERMS_ACCEPTED]: { BOOL: true },
          ...(data.fcm_token
            ? { [FCM_TOKEN]: { M: { 'web': { S: data.fcm_token } } } }
            : {})
        },
      },
    });

    requestItems.tenants.push({
      PutRequest: {
        Item: {
          // [UUID]: { S: data.id ? data.id : tenantUuid },
          [UUID]: { S: tenantUuid },
          [TENANT_NAME]: { S: userName },
          [TENANT_ID]: { S: uuidv4() },
          [CREATED_BY]: { S: updatedBy },
          [CREATED_AT]: { S: formattedDateTime },
          [MODIFIED_BY]: { S: updatedBy },
          [MODIFIED_AT]: { S: formattedDateTime },
          [DELETED_BY]: { S: '' },
          [DELETED_AT]: { S: '' }
        },
      },
    });
    requestItems.role_user.push({
      PutRequest: {
        Item: {
          [UUID]: { S: roleUserUuid },
          [ROLE_ID]: { S: userRoleID ? userRoleID : '' },
          [USER_ID]: { S: data.userId },
          [CREATED_BY]: { S: updatedBy },
          [CREATED_AT]: { S: formattedDateTime },
          [MODIFIED_BY]: { S: updatedBy },
          [MODIFIED_AT]: { S: formattedDateTime },
          [DELETED_BY]: { S: '' },
          [DELETED_AT]: { S: '' }
        },
      },
    });
  } else if (userRole.toLowerCase() === NURSE.toLowerCase()) {

    requestItems.user_info.push({
      PutRequest: {
        Item: {
          [UUID]: { S: data.userInfo_uuid ? data.userInfo_uuid : userInfoUuid },
          [NAME]: { S: userName },
          [ADDRESS_1]: { S: userAddressOne },
          [ADDRESS_2]: { S: userAddressTwo },
          [CITY]: { S: userCity },
          [STATE]: { S: userState },
          [DB_FIRST_LOGIN]: { S: 'true' },
          [COUNTRY]: { S: userCountry },
          [PINCODE]: { S: userPincode },
          [SLUG]: { S: userSlug },
          [ROLE]: { S: userRole },
          [DB_EMAIL]: { S: data.userEmail },
          [DB_MOBILE]: { S: data.userMobile },
          [DB_USER_DEVICE_ID]: { S: data.userDeviceId },
          [NURSE_ID]: { S: nurseId },
          [STATUS]: { S: data.status },
          [HOSPITAL_ID]: { S: data.hospitalId },
          [HOSPITAL_NAME]: { S: hospitalName },
          [USER_ID]: { S: data.userId },
          [TENANT_ID]: { S: tenantUuid },
          [CREATED_BY]: { S: updatedBy },
          [CREATED_AT]: { S: formattedDateTime },
          [MODIFIED_BY]: { S: updatedBy },
          [MODIFIED_AT]: { S: formattedDateTime },
          [DELETED_BY]: { S: '' },
          [DELETED_AT]: { S: '' },
          [APPROVED_BY]: { S: data.approvedBy },
          [TERMS_ACCEPTED]: { BOOL: true },
          ...(data.approvedBy != ''
            ? { [APPROVED_ON]: { S: formattedDateTime } }
            : {}),
          ...(data.fcm_token
            ? { [FCM_TOKEN]: { M: { 'web': { S: data.fcm_token } } } }
            : {})
        },
      },
    });

    requestItems.role_user.push({
      PutRequest: {
        Item: {
          [UUID]: { S: roleUserUuid },
          [ROLE_ID]: { S: userRoleID ? userRoleID : '' },
          [USER_ID]: { S: data.userId },
          [CREATED_BY]: { S: updatedBy },
          [CREATED_AT]: { S: formattedDateTime },
          [MODIFIED_BY]: { S: updatedBy },
          [MODIFIED_AT]: { S: formattedDateTime },
          [DELETED_BY]: { S: '' },
          [DELETED_AT]: { S: '' }
        },
      },
    });
  } else if (userRole.toLowerCase() === HOSPITAL.toLowerCase()) {
    requestItems.user_info.push({
      PutRequest: {
        Item: {
          // [UUID]: { S: data.userInfo_uuid ? data.userInfo_uuid : userInfoUuid },
          [UUID]: { S: userInfoUuid },
          [NAME]: { S: userName },
          [ADDRESS_1]: { S: userAddressOne },
          [ADDRESS_2]: { S: userAddressTwo },
          [CITY]: { S: userCity },
          [STATE]: { S: userState },
          [COUNTRY]: { S: userCountry },
          [STATUS]: { S: APPROVED },
          [PINCODE]: { S: userPincode },
          [DB_FIRST_LOGIN]: { S: 'true' },
          [SLUG]: { S: userSlug },
          [DB_EMAIL]: { S: data.userEmail },
          [DB_MOBILE]: { S: data.userMobile },
          [DB_USER_DEVICE_ID]: { S: data.userDeviceId },
          [ROLE]: { S: userRole },
          [HOSPITAL_ID]: { S: hospitalID },
          [HOSPITAL_NAME]: { S: hospitalName },
          [HOSPITAL_TELEPHONE]: { S: data.hospitalTelephoneNumber },
          [USER_ID]: { S: data.userId },
          [TENANT_ID]: { S: tenantUuid },
          [CREATED_BY]: { S: updatedBy },
          [CREATED_AT]: { S: formattedDateTime },
          [MODIFIED_BY]: { S: updatedBy },
          [MODIFIED_AT]: { S: formattedDateTime },
          [DELETED_BY]: { S: '' },
          [DELETED_AT]: { S: '' },
          [APPROVED_BY]: { S: data.approvedBy },
          [TERMS_ACCEPTED]: { BOOL: true },
          ...(data.approvedBy != ''
            ? { [APPROVED_ON]: { S: formattedDateTime } }
            : {}),
          ...(data.fcm_token
            ? { [FCM_TOKEN]: { M: { 'web': { S: data.fcm_token } } } }
            : {})
        },
      },
    });
    requestItems.tenants.push({
      PutRequest: {
        Item: {
          [UUID]: { S: tenantUuid },
          // [UUID]: { S: data.id ? data.id : tenantUuid },
          [TENANT_NAME]: { S: userName },
          [TENANT_ID]: { S: uuidv4() },
          [CREATED_BY]: { S: updatedBy },
          [CREATED_AT]: { S: formattedDateTime },
          [MODIFIED_BY]: { S: updatedBy },
          [MODIFIED_AT]: { S: formattedDateTime },
          [DELETED_BY]: { S: '' },
          [DELETED_AT]: { S: '' }
        },
      },
    });
    requestItems.hospital_tenant.push({
      PutRequest: {
        Item: {
          [UUID]: { S: hospitalTenantUuid },
          [HOSPITAL_ID]: { S: hospitalUuid },
          [TENANT_ID]: { S: tenantUuid },
          [CREATED_BY]: { S: updatedBy },
          [CREATED_AT]: { S: formattedDateTime },
          [MODIFIED_BY]: { S: updatedBy },
          [MODIFIED_AT]: { S: formattedDateTime },
          [DELETED_BY]: { S: '' },
          [DELETED_AT]: { S: '' }
        },
      },
    });

    requestItems.hospitals.push({
      PutRequest: {
        Item: {
          [UUID]: { S: hospitalUuid },
          [HOSPITAL_ID]: { S: hospitalID },
          [HOSPITAL_NAME]: { S: hospitalName },
          [STATUS]: { S: data.status },
          [TENANT_ID]: { S: tenantUuid },
          [CREATED_BY]: { S: updatedBy },
          [CREATED_AT]: { S: formattedDateTime },
          [MODIFIED_BY]: { S: updatedBy },
          [MODIFIED_AT]: { S: formattedDateTime },
          [DELETED_BY]: { S: '' },
          [DELETED_AT]: { S: '' }
        },
      },
    });
    requestItems.hospital_info.push({
      PutRequest: {
        Item: {
          // [UUID]: { S: data.hospitalInfo_uuid ? data.hospitalInfo_uuid : hospitalInfoUuid },
          [UUID]: { S: hospitalInfoUuid },
          [NAME]: { S: userName },
          [ADDRESS_1]: { S: userAddressOne },
          [ADDRESS_2]: { S: userAddressTwo },
          [CITY]: { S: userCity },
          [STATE]: { S: userState },
          [COUNTRY]: { S: userCountry },
          [PINCODE]: { S: userPincode },
          [STATUS]: { S: data.status },
          [HOSPITAL_GST]: { S: data.gst },
          [HOSPITAL_ID]: { S: hospitalID },
          [HOSPITAL_NAME]: { S: hospitalName },
          [HOSPITAL_BRANCH]: { S: data.hospitalBranch },
          [HOSPITAL_EMAIL]: { S: data.hospitalEmailID },
          [HOSPITAL_WEBSITE]: { S: data.hospitalWebsite },
          [HOSPITAL_CONTACT]: { S: data.hospitalContactNumber },
          [HOSPITAL_TELEPHONE]: { S: data.hospitalTelephoneNumber },
          [HOSPITAL_LOGO]: { S: logoName },
          [USER_ID]: { S: data.userId },
          [TENANT_ID]: { S: tenantUuid },
          [CREATED_BY]: { S: updatedBy },
          [CREATED_AT]: { S: formattedDateTime },
          [MODIFIED_BY]: { S: updatedBy },
          [MODIFIED_AT]: { S: formattedDateTime },
          [TERMS_ACCEPTED]: { BOOL: true },
          [DELETED_BY]: { S: '' },
          [DELETED_AT]: { S: '' }
        },
      },
    });
    requestItems.role_user.push({
      PutRequest: {
        Item: {
          [UUID]: { S: roleUserUuid },
          [ROLE_ID]: { S: userRoleID ? userRoleID : '' },
          [USER_ID]: { S: data.userId },
          [CREATED_BY]: { S: updatedBy },
          [CREATED_AT]: { S: formattedDateTime },
          [MODIFIED_BY]: { S: updatedBy },
          [MODIFIED_AT]: { S: formattedDateTime },
          [DELETED_BY]: { S: '' },
          [DELETED_AT]: { S: '' }
        },
      },
    });
    if (data.logoUrl) {
      await handleUpload(data, logoName);
    }
  }

  for (const tableName in requestItems) {
    const tableRequests = requestItems[tableName];
    while (tableRequests.length > 0) {
      const batch = tableRequests.splice(0, 25);
      const batchRequestItems = { [tableName]: batch };

      try {
        const response = await batchWriteCommand(batchRequestItems, data);
      } catch (error: any) {
        throw error
      }
    }
  }
}

export async function createAdminUser(data: any) {
  const formattedDateTime = generateUniqueTimestamp();
  const userInfoUuid = data.userInfo_uuid ? data.userInfo_uuid : uuidv4();
  const roleUserUuid = uuidv4();

  const userRole = HOSPITAL;
  const userRoleID = await getRolesForUser(userRole.toLowerCase());
  const userName = data.name;
  const userAddressOne = data.addressOne;
  const userAddressTwo = data.addressTwo;
  const userCity = data.city;
  const userState = data.state;
  const userCountry = data.country;
  const userPincode = data.pincode;
  const updatedBy = data.updatedBy;
  const hospitalID = data.hospitalId;
  const hospitalName = data.hospitalName;
  // const userSlug = generateSlug(userName);
  const userSlug = data.slug;
  const tenantUuid = data.tenant_id;

  let requestItems: RequestItems = {
    user_info: [],
    role_user: [],
  };
 
  if (userRole.toLowerCase() === HOSPITAL.toLowerCase()) {
    requestItems.user_info.push({
      PutRequest: {
        Item: {
          [UUID]: { S: data.userInfo_uuid ? data.userInfo_uuid : userInfoUuid },
          [NAME]: { S: userName },
          [ADDRESS_1]: { S: userAddressOne },
          [ADDRESS_2]: { S: userAddressTwo },
          [CITY]: { S: userCity },
          [STATE]: { S: userState },
          [COUNTRY]: { S: userCountry },
          [PINCODE]: { S: userPincode },
          [DB_FIRST_LOGIN]: { S: 'false' },
          [SLUG]: { S: userSlug },
          [DB_EMAIL]: { S: data.userEmail },
          [DB_MOBILE]: { S: data.userMobile },
          [DB_USER_DEVICE_ID]: { S: '' },
          [ROLE]: { S: userRole },
          [HOSPITAL_ID]: { S: hospitalID },
          [HOSPITAL_NAME]: { S: hospitalName },
          [HOSPITAL_TELEPHONE]: { S: data.hospitalTelephoneNumber },
          [USER_ID]: { S: data.userId },
          [TENANT_ID]: { S: tenantUuid },
          [CREATED_BY]: { S: updatedBy },
          [CREATED_AT]: { S: formattedDateTime },
          [MODIFIED_BY]: { S: updatedBy },
          [MODIFIED_AT]: { S: formattedDateTime },
          [DELETED_BY]: { S: '' },
          [DELETED_AT]: { S: '' },
          [STATUS]: { S: data.status },
          [APPROVED_BY]: { S: data.approvedBy }
        },
      },
    });
    requestItems.role_user.push({
      PutRequest: {
        Item: {
          [UUID]: { S: roleUserUuid },
          [ROLE_ID]: { S: userRoleID ? userRoleID : '' },
          [USER_ID]: { S: data.userId },
          [CREATED_BY]: { S: updatedBy },
          [CREATED_AT]: { S: formattedDateTime },
          [MODIFIED_BY]: { S: updatedBy },
          [MODIFIED_AT]: { S: formattedDateTime },
          [DELETED_BY]: { S: '' },
          [DELETED_AT]: { S: '' }
        },
      },
    });
  }

  for (const tableName in requestItems) {
    const tableRequests = requestItems[tableName];
    while (tableRequests.length > 0) {
      const batch = tableRequests.splice(0, 25);
      const batchRequestItems = { [tableName]: batch };

      try {
        const response = await batchWriteCommand(batchRequestItems, data);
      } catch (error: any) {
        throw error
      }
    }
  }
}

export async function handleUpload(data: any, logoName: string) {
  try {
    const name = logoName;
    // const content = data.logoUrl;
    const content = data.logoUrl.split(',')[1]; // Get the base64 part
    // const buffer = Buffer.from(content, 'base64');
    // console.log('buffer', buffer)
    let buffer;
    try {
      const binaryString = atob(content);
      const length = binaryString.length;
      buffer = new Uint8Array(length);
      for (let i = 0; i < length; i++) {
        buffer[i] = binaryString.charCodeAt(i);
      }
    } catch (bufferError) {
      console.error('Error converting base64 to buffer:', bufferError);
      return;
    }
    const type = data.logoType;
    const params = {
      Bucket: 'dripsetapplicationstaging',
      Key: name,
      Body: buffer,
      ContentType: type,
      ACL: ObjectCannedACL.private,
    };

    await s3Client.send(new PutObjectCommand(params));
  } catch (error: any) {
    // console.error('Error uploading file:', error);

    let errorMessage = 'An unexpected error occurred while uploading the file';

    switch (error.name) {
      case 'NoSuchBucket':
        errorMessage = 'The specified bucket does not exist';
        break;
      case 'AccessDenied':
        errorMessage = 'Access denied. Please check your permissions';
        break;
      case 'InvalidParameterException':
        errorMessage = 'Invalid parameters. Please check the file details and try again';
        break;
      case 'NetworkingError':
        errorMessage = 'Network error. Please check your internet connection and try again';
        break;
      default:
        errorMessage = error.message || 'An unknown error occurred';
    }

    // throw new Error(errorMessage);
    return errorMessage;
  }
}

async function updateTable(tableName: string, key: any, updateExpression: string, expressionAttributeNames: any, expressionAttributeValues: any) {
  const params: UpdateItemInput = {
    TableName: tableName,
    Key: key,
    UpdateExpression: updateExpression,
    ExpressionAttributeNames: expressionAttributeNames,
    ExpressionAttributeValues: expressionAttributeValues,
    ReturnValues: "ALL_NEW" // Optional parameter to return updated item
  };

  try {
    const command = new UpdateItemCommand(params);
    const response = await dbClient.send(command);
    // console.log(`Updated item in table ${tableName}:`, response.Attributes);
    return response.Attributes; // Return the updated item attributes if needed
  } catch (error) {
    console.error(`Error updating item in table ${tableName}:`, error);
    throw error;
  }
}

async function updateNurseDeviceTable(tableName: string, userId: string) {
  const queryParams: QueryCommandInput = {
      TableName: tableName,
      IndexName: USER_ID_CREATED_AT_INDEX,
      KeyConditionExpression: 'user_id = :user_id',
      FilterExpression: 'deleted_at = :deleted_at',
      ExpressionAttributeValues: {
        ':user_id': { S: userId },
        ':deleted_at': { S: '' },
      },
    };

  try {

    const queryResponse = await dbClient.send(new QueryCommand(queryParams));

    if(queryResponse.Items && queryResponse.Items.length > 0) {
      const key = queryResponse.Items[0];
      const dataUuid = key.uuid?.S;
      const tenantId = key.tenant_id?.S;

      if (!dataUuid) {
        return null;
      }
      if (!tenantId) {
        return null;
      }

      const formattedDateTime = generateUniqueTimestamp();
      let attributeNames = {
        '#deleted_at': 'deleted_at',
        '#deleted_by': 'deleted_by',
        '#modified_by': 'modified_by',
        '#modified_at': 'modified_at'
      };

      let attributeValues = {
      ':deleted_at': { S: formattedDateTime },
      ':deleted_by': { S: tenantId },
      ':modified_by': { S: tenantId },
      ':modified_at': { S: formattedDateTime },
      };
  
      let updateExpression =
      'SET #deleted_at = :deleted_at, #deleted_by = :deleted_by, #modified_by = :modified_by, #modified_at = :modified_at';


      const params: UpdateItemInput = {
        TableName: tableName,
        Key: {uuid: {S: dataUuid}},
        UpdateExpression: updateExpression,
        ExpressionAttributeNames: attributeNames,
        ExpressionAttributeValues: attributeValues,
        ReturnValues: "ALL_NEW" // Optional parameter to return updated item
    };

    const command = new UpdateItemCommand(params);
    const response = await dbClient.send(command);
    // console.log(`Updated item in table ${tableName}:`, response.Attributes);
    return response.Attributes; // Return the updated item attributes if needed
    } else {
      return null;
    }
  } catch (error) {
    console.error(`Error updating item in table ${tableName}:`, error);
    throw error;
  }
}

// let requestItems: RequestItems = {
//   user_info: [],
//   tenants: [],
//   hospital_tenant: [],
//   hospitals: [],
//   hospital_info: [],
// };

export async function handleDeleteUser(tenant_id: string, userId: string, role: string) {
  // console.log('data', tenant_id, userId)
  try {
    const formattedDateTime = generateUniqueTimestamp();
    const updateExpression = "SET #modified_by = :userId, #modified_at = :formattedDateTime, #deleted_by = :userId, #deleted_at = :formattedDateTime";
    const expressionAttributeNames = {
      "#modified_by": "modified_by",
      "#modified_at": "modified_at",
      "#deleted_by": "deleted_by",
      "#deleted_at": "deleted_at"
    };
    const expressionAttributeValues = {
      ":userId": { S: userId },
      ":formattedDateTime": { S: formattedDateTime }
    };

    if (role.toLowerCase() === INDIVIDUAL.toLowerCase() || role.toLowerCase() === NURSE.toLowerCase()) {
      const userUUID = await getTableDataUsingUserId('user_info', userId);

      if (userUUID && userUUID.length > 0) {
        const user = userUUID[0];
        const user_uuid = userUUID[0].uuid.S;
        const tenant_uuid = user.tenant_id?.S;

        await updateTable("user_info", { uuid: { S: user_uuid } }, updateExpression, expressionAttributeNames, expressionAttributeValues);

        if (role.toLowerCase() === INDIVIDUAL.toLowerCase()) {
          await updateTable("tenants", { uuid: { S: tenant_uuid } }, updateExpression, expressionAttributeNames, expressionAttributeValues);
        }

        if (role.toLowerCase() === NURSE.toLowerCase()) {
          await updateNurseDeviceTable("nurse_device", userId);
        }
      }

    } else if (role.toLowerCase() === HOSPITAL.toLowerCase()) {
      const userUUID = await getTableDataUsingUserId('user_info', userId);

      if (userUUID && userUUID.length > 0) {
        const user = userUUID[0];
        const user_uuid = userUUID[0].uuid.S;
        const tenant_uuid = user.tenant_id?.S ? user.tenant_id?.S : '';
        // const hospitalTenantsUUID = await getTableDataUsingTenantId('hospital_tenant', tenant_uuid);
        // const hospitalUUID = await getTableDataUsingTenantId('hospitals', tenant_uuid);
        // const hospitalInfoUUID = await getTableDataUsingTenantId('hospital_info', tenant_uuid);

        // if (hospitalTenantsUUID && hospitalUUID && hospitalInfoUUID && userUUID.length > 0 && hospitalTenantsUUID.length > 0 && hospitalUUID.length > 0 && hospitalInfoUUID.length > 0) {

          // if(userUUID && tenantsUUID ) {
          // const hospital_tenant_uuid = hospitalTenantsUUID[0].uuid.S;
          // const hospital_uuid = hospitalUUID[0].uuid.S;
          // const hospital_info_uuid = hospitalInfoUUID[0].uuid.S;
          // const logo_name = hospitalInfoUUID[0].hospital_logo.S;

          await updateTable("user_info", { uuid: { S: user_uuid } }, updateExpression, expressionAttributeNames, expressionAttributeValues);

        //   await updateTable("tenants", { uuid: { S: tenant_uuid } }, updateExpression, expressionAttributeNames, expressionAttributeValues);

        //   await updateTable("hospital_tenant", { uuid: { S: hospital_tenant_uuid } }, updateExpression, expressionAttributeNames, expressionAttributeValues);

        //   await updateTable("hospitals", { uuid: { S: hospital_uuid } }, updateExpression, expressionAttributeNames, expressionAttributeValues);

        //   await updateTable("hospital_info", { uuid: { S: hospital_info_uuid } }, updateExpression, expressionAttributeNames, expressionAttributeValues);

        //   if (logo_name) {
        //     await deleteS3Object(logo_name)
        //   }
        // }
      }

    }


    // Continue with updates for other tables as needed
  } catch (error: any) {
    console.error("Error updating multiple tables:", error);
    throw error;
  }
}

export async function handleDeleteAdminUser(user_id: string, id: string) {
  // console.log('data', tenant_id, userId)
  try {
    const formattedDateTime = generateUniqueTimestamp();
    const updateExpression = "SET #modified_by = :userId, #modified_at = :formattedDateTime, #deleted_by = :userId, #deleted_at = :formattedDateTime";
    const expressionAttributeNames = {
      "#modified_by": "modified_by",
      "#modified_at": "modified_at",
      "#deleted_by": "deleted_by",
      "#deleted_at": "deleted_at"
    };
    const expressionAttributeValues = {
      ":userId": { S: user_id },
      ":formattedDateTime": { S: formattedDateTime }
    };

      

          await updateTable("user_info", { uuid: { S: id } }, updateExpression, expressionAttributeNames, expressionAttributeValues);


    // Continue with updates for other tables as needed
  } catch (error: any) {
    console.error("Error updating multiple tables:", error);
    throw error;
  }
}

export async function scan(tableName: string, tenantId: string) {
  const params = {
    TableName: tableName,
    FilterExpression: "#tenantId = :tenantId AND #deleted_at = :deleted_at",
    ExpressionAttributeNames: {
      "#tenantId": "tenant_id",
      "#deleted_at": "deleted_at"
    },
    ExpressionAttributeValues: {
      ":tenantId": { S: tenantId },
      ":deleted_at": { S: '' }
    },
  };

  try {
    const command = new ScanCommand(params);
    const response = await dbClient.send(command);
    // console.log('scanHospitalInfo', response.Items);
    return response.Items;
  } catch (error) {
    console.error('Error scanning hospital info:', error);
    throw error;
  }
}

export async function getTableDataUsingUserId(tableName: string, userID: string) {
  const params = {
    TableName: tableName,
    IndexName: USER_ID_CREATED_AT_INDEX,
    KeyConditionExpression: '#user_id = :user_id',
    FilterExpression: 'deleted_at = :deleted_at',
    ExpressionAttributeNames: {
      '#user_id': 'user_id',
    },
    ExpressionAttributeValues: {
      ':user_id': { S: userID },
      ':deleted_at': { S: '' },
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);
    // console.log('scanHospitalInfo', response.Items);
    return response.Items;
  } catch (error) {
    throw error;
  }
}

async function getTableDataUsingTenantId(tableName: string, tenantId: string) {
  const params = {
    TableName: tableName,
    IndexName: TENANT_ID_CREATED_AT_INDEX,
    KeyConditionExpression: '#tenant_id = :tenant_id',
    FilterExpression: 'deleted_at = :deleted_at',
    ExpressionAttributeNames: {
      '#tenant_id': 'tenant_id',
    },
    ExpressionAttributeValues: {
      ':tenant_id': { S: tenantId },
      ':deleted_at': { S: '' },
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);
    // console.log('scanHospitalInfo', response.Items);
    return response.Items;
  } catch (error) {
    throw error;
  }
}

async function scanUuid(tableName: string, tenantId: string) {
  const params = {
    TableName: tableName,
    FilterExpression: "#uuid = :uuid AND #deleted_at = :deleted_at",
    ExpressionAttributeNames: {
      "#uuid": "uuid",
      "#deleted_at": "deleted_at"
    },
    ExpressionAttributeValues: {
      ":uuid": { S: tenantId },
      ":deleted_at": { S: '' }
    },
  };

  try {
    const command = new ScanCommand(params);
    const response = await dbClient.send(command);
    // console.log('scanHospitalInfo', response.Items);
    return response.Items;
  } catch (error) {
    console.error('Error scanning hospital info:', error);
    throw error;
  }
}

export async function deleteS3Object(objectKey: string) {
  const params = {
    Bucket: 'dripsetapplicationstaging',
    Key: objectKey,
  };

  try {
    const command = new DeleteObjectCommand(params);
    const response = await s3Client.send(command);
    // console.log('Delete successful', response);
    return response;
  } catch (error) {
    console.error('Error deleting object:', error);
    throw error;
  }
}

export async function addDeletionReason(updatedBy: string, reason: string, userId: string) {
  const formattedDateTime = generateUniqueTimestamp();
  const params = {
    TableName: "user_deletion_reasons",
    Item: {
      [UUID]: { S: uuidv4() },
      [REASONS]: { S: reason },
      [USER_ID]: { S: userId },
      [TENANT_ID]: { S: updatedBy },
      [CREATED_BY]: { S: updatedBy },
      [CREATED_AT]: { S: formattedDateTime },
      [MODIFIED_BY]: { S: updatedBy },
      [MODIFIED_AT]: { S: formattedDateTime },
      [DELETED_BY]: { S: '' },
      [DELETED_AT]: { S: '' }
    },
  };

  try {
    const data = await dbClient.send(new PutItemCommand(params));
    // console.log("Success - item added or updated", data);
  } catch (err) {
    console.error("Error", err);
  }
};

export async function addNotes(updatedBy: string, reason: string, userId: string, tenantId: string) {
  const formattedDateTime = generateUniqueTimestamp();
  const params = {
    TableName: "notes",
    Item: {
      [UUID]: { S: uuidv4() },
      [NOTES]: { S: reason },
      [USER_ID]: { S: userId },
      [TENANT_ID]: { S: tenantId },
      [CREATED_BY]: { S: updatedBy },
      [CREATED_AT]: { S: formattedDateTime },
      [MODIFIED_BY]: { S: updatedBy },
      [MODIFIED_AT]: { S: formattedDateTime },
      [DELETED_BY]: { S: '' },
      [DELETED_AT]: { S: '' }
    },
  };

  try {
    const data = await dbClient.send(new PutItemCommand(params));
    // console.log("Success - item added or updated", data);
  } catch (err) {
    console.error("Error", err);
  }
};


export async function adminDeleteUser(name: string) {
  const input = {
    UserPoolId: awsConfig.aws_user_pools_id,
    Username: name,
  };

  try {
    const command = new AdminDeleteUserCommand(input);
    const response = await client.send(command);

    // console.log('users', response)
  } catch (error) {
    console.error('Error Deleting user:', error);
  }

}

export async function hospitalList() {
  const params: ScanCommandInput = {
    TableName: 'hospitals',
  };

  try {
    let lastEvaluatedKey = undefined;
    const items = [];

    do {
      const command = new ScanCommand({
        ...params,
        ExclusiveStartKey: lastEvaluatedKey,
      });

      const response: ScanCommandOutput = await dbClient.send(command);
      const filteredItems = (response.Items || [])
        .filter(item => !item.deleted_at?.S)
        .map(item => ({
          uuid: item.uuid?.S || '',
          hospital_name: item.hospital_name?.S || '',
          hospital_id: item.hospital_id?.S || ''
        }));
      items.push(...filteredItems);
      lastEvaluatedKey = response.LastEvaluatedKey;
    } while (lastEvaluatedKey);
    // console.log('Items:', items);
    return items;
  } catch (error) {
    console.error('Error scanning hospital info:', error);
    throw error;
  }
}

export async function getHospitalList() {
  const params = {
    TableName: HOSPITALS_TABLE,
    IndexName: STATUS_INDEX,
    KeyConditionExpression: '#status = :status',
    FilterExpression: 'deleted_at = :deleted_at',
    ExpressionAttributeNames: {
      '#status': 'status',
    },
    ExpressionAttributeValues: {
      ':status': { S: APPROVED },
      ':deleted_at': { S: '' },
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);

    if (response && response.Items && response.Items.length > 0) {
      const filteredItems = (response.Items)
        .map(item => ({
          uuid: item.uuid?.S || '',
          hospital_name: item.hospital_name?.S || '',
          hospital_id: item.hospital_id?.S || ''
        }));
      return filteredItems;
    } else {
      return [];
    }

  } catch (error) {
    throw error;
  }
}

export async function checkHospitalList(hospitalId: string) {
  const params = {
    TableName: 'user_info',
    IndexName: HOSPITAL_ID_CREATED_AT_INDEX,
    KeyConditionExpression: '#hospital_id = :hospital_id',
    FilterExpression: 'deleted_at = :deleted_at AND #role = :role AND #status = :status',
    ExpressionAttributeNames: {
      '#hospital_id': 'hospital_id',
      '#status': 'status',
      '#role': 'role',
    },
    ExpressionAttributeValues: {
      ':hospital_id': { S: hospitalId },
      ':status': { S: APPROVED },
      ':deleted_at': { S: '' },
      ':role': { S: HOSPITAL },
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);

    return response.Items;
  } catch (error) {
    throw error;
  }
}
